import type { PerformanceSummary } from '@/pages/setup-fund/utils/type';

/**
 * Canonical headline return metric (GP-3781).
 *
 * Every investor-facing surface leads with the same window — the trailing 12
 * months of reported NAV — so the fund card and the fund page can never show two
 * different "return" numbers. Other windows (calendar year, since inception)
 * still exist, but only inside the performance-metrics grid where each one
 * carries its own caption.
 */
export const TRAILING_12M_RETURN_LABEL = '12-Mo Return';

export const TRAILING_12M_RETURN_TOOLTIP = 'Rolling 12-month net return from reported NAV';

/**
 * Shown only when a fund has no NAV history to derive a realised return from —
 * a target is never displayed alongside or instead of real performance.
 */
export const TARGET_RETURN_LABEL = 'Target Return';

export const TARGET_RETURN_TOOLTIP = "Manager's target, not realised performance";

const oneYearBefore = (date: Date) => {
  const anchor = new Date(date.getTime());
  anchor.setFullYear(anchor.getFullYear() - 1);

  return anchor;
};

/**
 * Trailing-12-month net return derived from the reported NAV series.
 *
 * Mirrors the backend rolling window that feeds fund cards
 * (`getOneYearReturnsForFunds`): the latest reported NAV against the most recent
 * NAV at least one year old, both anchored on *today* rather than on the latest
 * report — so a fund with stale NAVs yields the same figure here as it does on
 * its card.
 *
 * `netReturnSeries` carries cumulative return since inception at each reported
 * date, so `(1 + latest) / (1 + old) - 1` reduces to `navLatest / navOld - 1`:
 * algebraically the same ratio the backend computes from raw NAV values.
 *
 * Returns a fraction (`0.166` = 16.6%) to match every other `performanceSummary`
 * field, or `null` when the fund has no reported NAV old enough to measure a
 * year against.
 */
export const getTrailingTwelveMonthReturn = (
  summary: PerformanceSummary | null | undefined,
  now: Date = new Date(),
): number | null => {
  const series = summary?.netReturnSeries;

  if (!series || series.length < 2) return null;

  const cutoff = oneYearBefore(now).getTime();

  const points = series
    .map((point) => ({ time: new Date(point.date).getTime(), return: point.return }))
    .filter((point) => Number.isFinite(point.time) && Number.isFinite(point.return))
    .sort((a, b) => a.time - b.time);

  if (points.length < 2) return null;

  const latest = points[points.length - 1];

  // Most recent point that is already at least a year old, matching the
  // backend's `date <= NOW() - INTERVAL '1 year'` cutoff.
  let base: (typeof points)[number] | undefined;
  for (const point of points) {
    if (point.time <= cutoff) base = point;
  }

  if (!base || base.time === latest.time) return null;

  const baseFactor = 1 + base.return;
  if (baseFactor <= 0) return null;

  const trailingReturn = (1 + latest.return) / baseFactor - 1;

  return Number.isFinite(trailingReturn) ? trailingReturn : null;
};
